// 天气和节日事件系统 - 随机事件、天气变化、节日活动
import { GameState } from './GameState.js';
import { FloatingTextSystem } from './FloatingText.js';

// 天气类型
export enum WeatherType {
  SUNNY = 'sunny',
  CLOUDY = 'cloudy',
  RAINY = 'rainy',
  SNOWY = 'snowy',
  FOGGY = 'foggy',
  WINDY = 'windy'
}

// 天气配置
export const WEATHER_CONFIG: Record<WeatherType, {
  name: string;
  emoji: string;
  color: string;
  bgColor: string;
  duration: [number, number]; // 持续时间范围（秒）
  probability: number;
  effects: {
    coinBonus: number;
    movementSpeed: number;
    npcActivity: number;
  };
}> = {
  [WeatherType.SUNNY]: {
    name: '晴朗',
    emoji: '☀️',
    color: '#FFD700',
    bgColor: '#87CEEB',
    duration: [60, 120],
    probability: 40,
    effects: { coinBonus: 1.2, movementSpeed: 1, npcActivity: 1 }
  },
  [WeatherType.CLOUDY]: {
    name: '多云',
    emoji: '⛅',
    color: '#B0C4DE',
    bgColor: '#B0C4DE',
    duration: [45, 90],
    probability: 25,
    effects: { coinBonus: 1, movementSpeed: 1, npcActivity: 0.9 }
  },
  [WeatherType.RAINY]: {
    name: '下雨',
    emoji: '🌧️',
    color: '#4682B4',
    bgColor: '#5F7A8A',
    duration: [30, 60],
    probability: 15,
    effects: { coinBonus: 0.8, movementSpeed: 0.8, npcActivity: 0.5 }
  },
  [WeatherType.SNOWY]: {
    name: '下雪',
    emoji: '❄️',
    color: '#E0FFFF',
    bgColor: '#D3D3D3',
    duration: [40, 80],
    probability: 10,
    effects: { coinBonus: 1.5, movementSpeed: 0.7, npcActivity: 0.6 }
  },
  [WeatherType.FOGGY]: {
    name: '大雾',
    emoji: '🌫️',
    color: '#D3D3D3',
    bgColor: '#A9A9A9',
    duration: [20, 40],
    probability: 5,
    effects: { coinBonus: 0.9, movementSpeed: 0.9, npcActivity: 0.3 }
  },
  [WeatherType.WINDY]: {
    name: '大风',
    emoji: '🌬️',
    color: '#98FB98',
    bgColor: '#90EE90',
    duration: [25, 50],
    probability: 5,
    effects: { coinBonus: 1.1, movementSpeed: 1.1, npcActivity: 0.7 }
  }
};

// 节日类型
export enum FestivalType {
  SPRING_FESTIVAL = 'spring_festival', // 春节
  LANTERN_FESTIVAL = 'lantern_festival', // 元宵节
  DRAGON_BOAT = 'dragon_boat', // 端午节
  MID_AUTUMN = 'mid_autumn', // 中秋节
  BIRTHDAY = 'birthday', // 生日
  NEW_YEAR = 'new_year', // 元旦
  LABOR_DAY = 'labor_day', // 劳动节
  NATIONAL_DAY = 'national_day' // 国庆节
}

// 节日配置
export const FESTIVAL_CONFIG: Record<FestivalType, {
  name: string;
  emoji: string;
  description: string;
  month: number;
  day: number;
  duration: number; // 持续天数
  rewards: {
    coins: number;
    items: string[];
    friendshipBonus: number;
  };
  activities: string[];
}> = {
  [FestivalType.SPRING_FESTIVAL]: {
    name: '春节',
    emoji: '🧧',
    description: '新春佳节，阖家团圆',
    month: 1,
    day: 1,
    duration: 7,
    rewards: {
      coins: 888,
      items: ['红包', '春联', '糖果'],
      friendshipBonus: 20
    },
    activities: ['拜年', '放鞭炮', '吃年夜饭', '看春晚']
  },
  [FestivalType.LANTERN_FESTIVAL]: {
    name: '元宵节',
    emoji: '🏮',
    description: '正月十五，赏花灯',
    month: 1,
    day: 15,
    duration: 3,
    rewards: {
      coins: 200,
      items: ['汤圆', '花灯'],
      friendshipBonus: 10
    },
    activities: ['猜灯谜', '吃汤圆', '赏花灯']
  },
  [FestivalType.DRAGON_BOAT]: {
    name: '端午节',
    emoji: '🐲',
    description: '五月初五，赛龙舟',
    month: 5,
    day: 5,
    duration: 3,
    rewards: {
      coins: 300,
      items: ['粽子', '香囊'],
      friendshipBonus: 15
    },
    activities: ['吃粽子', '赛龙舟', '挂艾草']
  },
  [FestivalType.MID_AUTUMN]: {
    name: '中秋节',
    emoji: '🥮',
    description: '八月十五，赏月团圆',
    month: 8,
    day: 15,
    duration: 3,
    rewards: {
      coins: 400,
      items: ['月饼', '柚子'],
      friendshipBonus: 15
    },
    activities: ['赏月', '吃月饼', '拜月']
  },
  [FestivalType.BIRTHDAY]: {
    name: '生日',
    emoji: '🎂',
    description: '欢欢的生日',
    month: 6,
    day: 1, // 假设6月1日
    duration: 1,
    rewards: {
      coins: 666,
      items: ['生日蛋糕', '礼物', '气球'],
      friendshipBonus: 30
    },
    activities: ['吹蜡烛', '许愿', '吃蛋糕', '收礼物']
  },
  [FestivalType.NEW_YEAR]: {
    name: '元旦',
    emoji: '🎉',
    description: '新年新气象',
    month: 1,
    day: 1,
    duration: 1,
    rewards: {
      coins: 200,
      items: ['新年贺卡'],
      friendshipBonus: 10
    },
    activities: ['跨年', '许愿', '贴窗花']
  },
  [FestivalType.LABOR_DAY]: {
    name: '劳动节',
    emoji: '👷',
    description: '劳动最光荣',
    month: 5,
    day: 1,
    duration: 3,
    rewards: {
      coins: 150,
      items: ['红花'],
      friendshipBonus: 5
    },
    activities: ['帮奶奶干活', '种菜', '打扫院子']
  },
  [FestivalType.NATIONAL_DAY]: {
    name: '国庆节',
    emoji: '🇨🇳',
    description: '祖国生日快乐',
    month: 10,
    day: 1,
    duration: 7,
    rewards: {
      coins: 500,
      items: ['国旗', '小红旗'],
      friendshipBonus: 15
    },
    activities: ['看升旗', '游山玩水', '吃好吃的']
  }
};

// 随机事件类型
export enum RandomEventType {
  FIND_MONEY = 'find_money', // 捡到钱
  DOG_BRINGS_GIFT = 'dog_brings_gift', // 丹丹带来礼物
  BIRD_SINGS = 'bird_sings', // 小鸟唱歌
  GRANDMA_COOKS = 'grandma_cooks', // 奶奶做了好吃的
  RAINBOW = 'rainbow', // 出现彩虹
  SHOOTING_STAR = 'shooting_star', // 流星
  STRANGE_DREAM = 'strange_dream', // 奇怪的梦
  LOST_ITEM = 'lost_item', // 丢失物品
  FRIEND_VISITS = 'friend_visits', // 朋友来访
  MARKET_DISCOUNT = 'market_discount' // 集市打折
}

// 随机事件配置
export const RANDOM_EVENTS: Record<RandomEventType, {
  name: string;
  emoji: string;
  description: string;
  probability: number;
  cooldown: number;
  rewards?: {
    coins?: number;
    items?: string[];
    happiness?: number;
  };
}> = {
  [RandomEventType.FIND_MONEY]: {
    name: '捡到钱',
    emoji: '💰',
    description: '你在路上捡到了一些钱！',
    probability: 0.05,
    cooldown: 300,
    rewards: { coins: 50, happiness: 5 }
  },
  [RandomEventType.DOG_BRINGS_GIFT]: {
    name: '丹丹的礼物',
    emoji: '🎁',
    description: '丹丹叼来了一个小礼物！',
    probability: 0.03,
    cooldown: 600,
    rewards: { items: ['树枝', '石头', '瓶盖'], happiness: 10 }
  },
  [RandomEventType.BIRD_SINGS]: {
    name: '小鸟唱歌',
    emoji: '🐦',
    description: '树上的小鸟唱起了好听的歌',
    probability: 0.08,
    cooldown: 180,
    rewards: { happiness: 15 }
  },
  [RandomEventType.GRANDMA_COOKS]: {
    name: '奶奶的美食',
    emoji: '🍲',
    description: '奶奶做了你最爱吃的菜！',
    probability: 0.04,
    cooldown: 900,
    rewards: { happiness: 20, coins: 30 }
  },
  [RandomEventType.RAINBOW]: {
    name: '彩虹',
    emoji: '🌈',
    description: '雨后天空出现了美丽的彩虹！',
    probability: 0.02,
    cooldown: 1200,
    rewards: { happiness: 25 }
  },
  [RandomEventType.SHOOTING_STAR]: {
    name: '流星',
    emoji: '🌠',
    description: '夜空中划过一颗流星，快许愿！',
    probability: 0.01,
    cooldown: 3600,
    rewards: { coins: 100, happiness: 30 }
  },
  [RandomEventType.STRANGE_DREAM]: {
    name: '奇怪的梦',
    emoji: '💭',
    description: '昨晚做了一个奇怪的梦...',
    probability: 0.06,
    cooldown: 600,
    rewards: { happiness: 5 }
  },
  [RandomEventType.LOST_ITEM]: {
    name: '丢失物品',
    emoji: '🔍',
    description: '你好像丢了什么东西...',
    probability: 0.03,
    cooldown: 600,
    rewards: { coins: -20, happiness: -5 }
  },
  [RandomEventType.FRIEND_VISITS]: {
    name: '朋友来访',
    emoji: '👫',
    description: '小明来找你玩了！',
    probability: 0.04,
    cooldown: 600,
    rewards: { happiness: 15, coins: 20 }
  },
  [RandomEventType.MARKET_DISCOUNT]: {
    name: '集市打折',
    emoji: '🛍️',
    description: '今天集市大减价！',
    probability: 0.05,
    cooldown: 1200,
    rewards: { coins: 100 }
  }
};

// 天气和事件系统管理器
export class WeatherEventSystem {
  private gameState: GameState;
  private floatingText?: FloatingTextSystem;
  
  // 当前天气
  private currentWeather: WeatherType = WeatherType.SUNNY;
  private weatherStartTime: number = Date.now();
  private weatherDuration: number = 60;
  
  // 当前节日
  private activeFestivals: FestivalType[] = [];
  
  // 事件冷却
  private eventCooldowns: Map<RandomEventType, number> = new Map();
  
  // 游戏内时间
  private gameDay: number = 1;
  private gameHour: number = 8;
  private gameMinute: number = 0;
  private timeMultiplier: number = 10; // 游戏时间流逝速度
  
  // 天气粒子
  private weatherParticles: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    opacity: number;
  }> = [];
  
  // 事件监听器
  private onWeatherChange?: (weather: WeatherType) => void;
  private onFestivalStart?: (festival: FestivalType) => void;
  private onRandomEvent?: (event: RandomEventType) => void;
  
  constructor(gameState: GameState) {
    this.gameState = gameState;
    this.initWeather();
  }
  
  // 设置回调
  setCallbacks(callbacks: {
    onWeatherChange?: (weather: WeatherType) => void;
    onFestivalStart?: (festival: FestivalType) => void;
    onRandomEvent?: (event: RandomEventType) => void;
  }): void {
    this.onWeatherChange = callbacks.onWeatherChange;
    this.onFestivalStart = callbacks.onFestivalStart;
    this.onRandomEvent = callbacks.onRandomEvent;
  }
  
  // 设置飘字系统
  setFloatingText(system: FloatingTextSystem): void {
    this.floatingText = system;
  }
  
  // 初始化天气
  private initWeather(): void {
    this.changeWeather(WeatherType.SUNNY);
  }
  
  // 更新
  update(dt: number): void {
    // 更新游戏时间
    this.updateGameTime(dt);
    
    // 更新天气
    this.updateWeather(dt);
    
    // 检查节日
    this.checkFestivals();
    
    // 更新天气粒子
    this.updateWeatherParticles(dt);
  }
  
  // 更新游戏时间
  private updateGameTime(dt: number): void {
    const realSecondsPerGameHour = 120; // 2分钟 = 游戏内1小时
    const gameMinutesPassed = (dt * this.timeMultiplier * 60) / realSecondsPerGameHour;
    
    this.gameMinute += gameMinutesPassed;
    
    while (this.gameMinute >= 60) {
      this.gameMinute -= 60;
      this.gameHour++;
    }
    
    while (this.gameHour >= 24) {
      this.gameHour -= 24;
      this.gameDay++;
    }
  }
  
  // 更新天气
  private updateWeather(dt: number): void {
    const elapsed = (Date.now() - this.weatherStartTime) / 1000;
    
    if (elapsed >= this.weatherDuration) {
      // 根据概率选择新天气
      const weatherTypes = Object.values(WeatherType);
      const random = Math.random() * 100;
      let cumulative = 0;
      
      for (const weather of weatherTypes) {
        cumulative += WEATHER_CONFIG[weather].probability;
        if (random <= cumulative) {
          this.changeWeather(weather);
          break;
        }
      }
    }
  }
  
  // 改变天气
  changeWeather(weather: WeatherType): void {
    const oldWeather = this.currentWeather;
    this.currentWeather = weather;
    this.weatherStartTime = Date.now();
    this.weatherDuration = WEATHER_CONFIG[weather].duration[0] + 
      Math.random() * (WEATHER_CONFIG[weather].duration[1] - WEATHER_CONFIG[weather].duration[0]);
    
    // 显示天气变化提示
    if (oldWeather !== weather && this.floatingText) {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 4;
      const config = WEATHER_CONFIG[weather];
      this.floatingText.spawn(centerX, centerY, `${config.emoji} 天气变了：${config.name}`, 'info');
    }
    
    if (this.onWeatherChange) {
      this.onWeatherChange(weather);
    }
    
    // 初始化天气粒子
    this.initWeatherParticles();
  }
  
  // 初始化天气粒子
  private initWeatherParticles(): void {
    this.weatherParticles = [];
    const particleCount = this.currentWeather === WeatherType.RAINY ? 100 :
                          this.currentWeather === WeatherType.SNOWY ? 80 :
                          this.currentWeather === WeatherType.FOGGY ? 20 : 0;
    
    for (let i = 0; i < particleCount; i++) {
      this.spawnWeatherParticle();
    }
  }
  
  // 生成天气粒子
  private spawnWeatherParticle(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    this.weatherParticles.push({
      x: Math.random() * width,
      y: Math.random() * height - height,
      vx: this.currentWeather === WeatherType.WINDY ? 2 + Math.random() * 3 : 
          this.currentWeather === WeatherType.RAINY ? 0.5 : 0,
      vy: this.currentWeather === WeatherType.RAINY ? 8 + Math.random() * 4 :
          this.currentWeather === WeatherType.SNOWY ? 1 + Math.random() * 2 :
          this.currentWeather === WeatherType.FOGGY ? 0.2 : 2,
      size: this.currentWeather === WeatherType.SNOWY ? 2 + Math.random() * 3 :
            this.currentWeather === WeatherType.FOGGY ? 20 + Math.random() * 30 : 1 + Math.random() * 2,
      opacity: 0.3 + Math.random() * 0.5
    });
  }
  
  // 更新天气粒子
  private updateWeatherParticles(dt: number): void {
    const height = window.innerHeight;
    const width = window.innerWidth;
    
    this.weatherParticles.forEach(p => {
      p.x += p.vx;
      p.y += p.vy;
      
      // 重置粒子
      if (p.y > height) {
        p.y = -p.size;
        p.x = Math.random() * width;
      }
      if (p.x > width) {
        p.x = 0;
      }
    });
    
    // 保持粒子数量
    const targetCount = this.currentWeather === WeatherType.RAINY ? 100 :
                        this.currentWeather === WeatherType.SNOWY ? 80 :
                        this.currentWeather === WeatherType.FOGGY ? 20 : 0;
    
    while (this.weatherParticles.length < targetCount) {
      this.spawnWeatherParticle();
    }
  }
  
  // 绘制天气效果
  renderWeather(ctx: CanvasRenderingContext2D): void {
    const config = WEATHER_CONFIG[this.currentWeather];
    
    // 绘制天气遮罩
    if (this.currentWeather !== WeatherType.SUNNY) {
      ctx.fillStyle = config.bgColor + '40'; // 25%透明度
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }
    
    // 绘制粒子
    ctx.save();
    this.weatherParticles.forEach(p => {
      ctx.globalAlpha = p.opacity;
      
      if (this.currentWeather === WeatherType.RAINY) {
        ctx.strokeStyle = '#A0A0A0';
        ctx.lineWidth = p.size;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x + p.vx, p.y + p.vy * 2);
        ctx.stroke();
      } else if (this.currentWeather === WeatherType.SNOWY) {
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      } else if (this.currentWeather === WeatherType.FOGGY) {
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    ctx.restore();
  }
  
  // 检查节日
  private checkFestivals(): void {
    const now = new Date();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    
    // 注意：这里使用真实日期，但在游戏中可以调整
    const festivals = Object.values(FestivalType);
    
    festivals.forEach(festival => {
      const config = FESTIVAL_CONFIG[festival];
      
      // 检查是否在节日期间
      const festivalStart = new Date(now.getFullYear(), config.month - 1, config.day);
      const festivalEnd = new Date(festivalStart);
      festivalEnd.setDate(festivalEnd.getDate() + config.duration);
      
      if (now >= festivalStart && now <= festivalEnd) {
        if (!this.activeFestivals.includes(festival)) {
          this.activeFestivals.push(festival);
          this.onFestivalStart?.(festival);
        }
      } else {
        const index = this.activeFestivals.indexOf(festival);
        if (index > -1) {
          this.activeFestivals.splice(index, 1);
        }
      }
    });
  }
  
  // 触发随机事件
  triggerRandomEvent(): RandomEventType | null {
    const now = Date.now();
    const availableEvents: RandomEventType[] = [];
    
    (Object.keys(RANDOM_EVENTS) as RandomEventType[]).forEach((type) => {
      const config = RANDOM_EVENTS[type];
      const cooldownEnd = (this.eventCooldowns.get(type) || 0) + config.cooldown * 1000;
      if (now > cooldownEnd && Math.random() < config.probability) {
        availableEvents.push(type);
      }
    });
    
    if (availableEvents.length === 0) return null;
    
    const event = availableEvents[Math.floor(Math.random() * availableEvents.length)];
    this.eventCooldowns.set(event, now);
    
    // 应用事件奖励
    const config = RANDOM_EVENTS[event];
    if (config.rewards) {
      if (config.rewards.coins) {
        // 金币奖励在CanvasGame中处理
      }
      if (config.rewards.happiness) {
        this.gameState.addToAttribute('happiness', config.rewards.happiness);
      }
    }
    
    // 显示事件
    if (this.floatingText) {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 3;
      this.floatingText.spawn(centerX, centerY, `${config.emoji} ${config.name}`, 'achievement');
    }
    
    this.onRandomEvent?.(event);
    return event;
  }
  
  // 获取当前天气
  getCurrentWeather(): WeatherType {
    return this.currentWeather;
  }
  
  // 获取天气配置
  getWeatherConfig(weather?: WeatherType): typeof WEATHER_CONFIG[WeatherType] {
    return WEATHER_CONFIG[weather || this.currentWeather];
  }
  
  // 获取当前节日
  getActiveFestivals(): FestivalType[] {
    return [...this.activeFestivals];
  }
  
  // 是否是节日
  isFestivalActive(festival?: FestivalType): boolean {
    if (festival) {
      return this.activeFestivals.includes(festival);
    }
    return this.activeFestivals.length > 0;
  }
  
  // 获取节日奖励
  getFestivalRewards(): { coins: number; friendshipBonus: number } {
    let totalCoins = 0;
    let totalFriendship = 0;
    
    this.activeFestivals.forEach(festival => {
      const config = FESTIVAL_CONFIG[festival];
      totalCoins += config.rewards.coins;
      totalFriendship += config.rewards.friendshipBonus;
    });
    
    return { coins: totalCoins, friendshipBonus: totalFriendship };
  }
  
  // 获取游戏时间
  getGameTime(): { day: number; hour: number; minute: number } {
    return {
      day: this.gameDay,
      hour: this.gameHour,
      minute: Math.floor(this.gameMinute)
    };
  }
  
  // 获取天气效果
  getWeatherEffects(): { coinBonus: number; movementSpeed: number; npcActivity: number } {
    return WEATHER_CONFIG[this.currentWeather].effects;
  }
  
  // 导出数据
  export(): object {
    return {
      currentWeather: this.currentWeather,
      weatherStartTime: this.weatherStartTime,
      weatherDuration: this.weatherDuration,
      activeFestivals: this.activeFestivals,
      gameDay: this.gameDay,
      gameHour: this.gameHour,
      gameMinute: this.gameMinute,
      eventCooldowns: Object.fromEntries(this.eventCooldowns)
    };
  }
  
  // 导入数据
  import(data: any): void {
    if (data.currentWeather) {
      this.currentWeather = data.currentWeather;
    }
    if (data.weatherStartTime) {
      this.weatherStartTime = data.weatherStartTime;
    }
    if (data.weatherDuration) {
      this.weatherDuration = data.weatherDuration;
    }
    if (data.activeFestivals) {
      this.activeFestivals = data.activeFestivals;
    }
    if (data.gameDay) this.gameDay = data.gameDay;
    if (data.gameHour) this.gameHour = data.gameHour;
    if (data.gameMinute) this.gameMinute = data.gameMinute;
    if (data.eventCooldowns) {
      this.eventCooldowns = new Map(Object.entries(data.eventCooldowns) as [RandomEventType, number][]);
    }
    this.initWeatherParticles();
  }
}
